import * as Sentry from '@sentry/browser';
import { createJSONStorage, type PersistStorage, type StorageValue } from 'zustand/middleware';

/** Keys already warned about on the console, so the log stays readable. */
const warnedKeys = new Set<string>();

/**
 * Keys already sent to Sentry. A persisted store writes on every `setState`, so
 * an unguarded report would emit one event per keystroke; the first failure per
 * key carries all the signal we need.
 */
const reportedKeys = new Set<string>();

/**
 * Reports a Web Storage failure to the console once, and to Sentry once a client exists.
 *
 * The Sentry gate is load-bearing rather than defensive. `main.tsx` imports the
 * app *before* it calls `Sentry.init`, and ES modules evaluate imports first, so
 * all 13 stores are created — and synchronously hydrated, including the
 * post-migration rewrite — while `getClient()` is still undefined. Sentry drops a
 * capture with no client (`scope.captureException` returns early), so marking the
 * key as reported at that point would permanently mute the very failure this
 * module exists to surface. Leaving it unmarked lets the next failure, after init,
 * through.
 *
 * The whole body is wrapped: telling someone about a dropped write must never be
 * the thing that breaks the write. A throwing console (patched by an extension) or
 * a throwing Sentry transport would otherwise escape `setItem`, escape `setState`,
 * and reproduce exactly the crash this module exists to prevent.
 *
 * @param {string} name - The storage key whose read or write failed
 * @param {unknown} error - The error thrown by the underlying Storage call
 * @returns {void}
 */
function reportOnce(name: string, error: unknown): void {
  try {
    if (!warnedKeys.has(name)) {
      warnedKeys.add(name);
      console.warn(`[persist-storage] '${name}' could not be persisted; continuing with in-memory state only.`, error);
    }

    if (reportedKeys.has(name) || !Sentry.isInitialized()) {
      return;
    }
    reportedKeys.add(name);

    Sentry.captureException(error, {
      tags: { persistedStorageKey: name, storageQuotaExceeded: isQuotaExceededError(error) },
    });
  } catch {
    // Nothing left to report to, and the caller has already dropped the write.
  }
}

/** Error names browsers use to signal that Web Storage is full. */
const QUOTA_ERROR_NAMES = new Set(['QuotaExceededError', 'NS_ERROR_DOM_QUOTA_REACHED']);

/** Legacy numeric codes for the same condition, kept for older Firefox. */
const QUOTA_ERROR_CODES = new Set([22, 1014]);

/**
 * Distinguishes "the origin is out of space" from "storage is unavailable".
 *
 * Both are dropped the same way, but they need different responses from us: a full
 * origin means some store is persisting too much and wants a `partialize` fix,
 * whereas blocked site data or private browsing is the user's own setting and
 * needs nothing. The distinction rides along as a Sentry tag so the two cannot be
 * mistaken for each other in triage.
 *
 * @param {unknown} error - The error thrown by a Storage call
 * @returns {boolean} True when the write failed because the origin is full
 */
function isQuotaExceededError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const { name, code } = error as { name?: unknown; code?: unknown };

  return (
    (typeof name === 'string' && QUOTA_ERROR_NAMES.has(name)) ||
    (typeof code === 'number' && QUOTA_ERROR_CODES.has(code))
  );
}

/**
 * Performs one write, returning the outcome instead of propagating a throw.
 *
 * Returns a discriminated result rather than the error itself so that a falsy
 * thrown value (`throw null`) is still reported as a failure.
 *
 * @param {PersistStorage<S>} storage - The underlying zustand JSON storage
 * @param {string} name - The storage key to write
 * @param {StorageValue<S>} value - The versioned state envelope zustand hands us
 * @returns {{ ok: true } | { ok: false; error: unknown }} Whether the write landed
 */
function attemptWrite<S>(
  storage: PersistStorage<S>,
  name: string,
  value: StorageValue<S>,
): { ok: true } | { ok: false; error: unknown } {
  try {
    storage.setItem(name, value);
    return { ok: true };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * Builds a zustand `PersistStorage` whose writes can never crash the app.
 *
 * MDN's "Storage quotas and eviction criteria" states that browsers "can store up
 * to 5 MiB of local storage ... per origin", and that once the limit is reached
 * they "throw a `QuotaExceededError` exception which should be handled by using a
 * try...catch block". zustand's persist middleware does no such thing — `api.setState` calls
 * `setItem()` directly — so an unguarded write turns an ordinary `setState` into a
 * throw. Two failure modes follow, both of which look like a blank grey page:
 * thrown from a `useEffect` React "will remove its UI from the screen", and thrown
 * inside an async flow it skips every remaining step (such as the post-login
 * `navigate`). Wrapping the storage instead of the call sites fixes both at once,
 * and is the approach zustand's own maintainers recommend for quota errors.
 *
 * A failed write is dropped, never retried, and the previous value is left alone.
 * Retrying after evicting the key looks tempting but cannot work: Web Storage
 * measures the quota on the *delta*, summing every entry except the one being
 * written (jsdom `Storage-impl`; Chromium `StorageAreaImpl::Put` computes
 * `storage_used_ - old_item_size + new_item_size`). Evicting therefore leaves the
 * failing inequality unchanged, so the retry throws again — having already deleted
 * the last value that did fit. That would trade this module's crash for silent loss
 * of the user's saved preferences. The same arithmetic means a *smaller* value
 * always fits, so the `migrate` reclaim in a bloated store needs no eviction either.
 *
 * This is a backstop, not a licence to persist large payloads: server data belongs
 * in React Query, and `partialize` should allow-list the UI state worth keeping.
 *
 * @param {() => Storage} getStorage - Accessor for the backing store, e.g. `() => localStorage`
 * @returns {PersistStorage<S> | undefined} A crash-proof storage, or `undefined` when the
 *   backing store is unreachable (Safari private mode, blocked site data), which makes
 *   zustand skip persistence for that store exactly as it does today
 */
function createSafeJSONStorage<S>(getStorage: () => Storage): PersistStorage<S> | undefined {
  const base = createJSONStorage<S>(getStorage);

  if (!base) {
    return undefined;
  }

  return {
    getItem: (name) => {
      try {
        return base.getItem(name);
      } catch (error) {
        // A corrupt or unreadable blob must degrade to "nothing persisted" rather
        // than reject hydration, so the store still reaches its default state.
        reportOnce(name, error);
        return null;
      }
    },

    setItem: (name, value) => {
      const attempt = attemptWrite(base, name, value);

      if (!attempt.ok) {
        // Drop this write and keep whatever already fits: see the note above on
        // why evicting to make room cannot succeed.
        reportOnce(name, attempt.error);
      }
    },

    removeItem: (name) => {
      try {
        base.removeItem(name);
      } catch (error) {
        reportOnce(name, error);
      }
    },
  };
}

export { createSafeJSONStorage };
